import React, { useCallback, useEffect, useState } from "react";
import { Virtuoso } from "react-virtuoso";
import styled from "styled-components";
import useCloseHook from "../../../../lib/closeHook";
import { Column, InputBox } from "../../atoms";
import { giphy_search, SEARCH_LIMIT } from "./GiphyApi";

interface Props {
    /**
     * Select gif handler
     */
    onSelect?: (file_url: string) => void;

    /**
     * Handle clicking outside of picker
     */
    onClose?: () => void;
}

/**
 * Hard-coded row size
 * ! FIXME: this will be calculated automatically later I guess
 */
const ROW_SIZE = 8;

/**
 * Base layout of the picker
 */
const Base = styled(Column)`
    overflow: hidden;
    user-select: none;
    position: absolute;

    right: 10px;
    bottom: 10px;

    // row width + scrollbar + group selector
    width: calc(${ROW_SIZE} * 40px + 10px + 40px);
    height: 600px;

    max-width: calc(100vw - 20px);
    max-height: calc(75vh);

    background: var(--background);
    border-radius: var(--border-radius);
`;

/**
 * Top search controls parent component
 */
const Controls = styled(Column)`
    padding: 0.5em;
`;

/**
 * Picker parent component
 */
const Parent = styled.div`
    flex-grow: 1;
    min-height: 0;

    display: flex;
    flex-direction: row;
`;

// Limit number of giphy api calls
const DEBOUNCE_TIMEOUT = 2 * 1000;

/**
 * GIF Picker
 */
export function GifPicker({ onSelect, onClose }: Props) {
    const [query, setQuery] = useState("");
    const [gifs, setGifs] = useState([] as any[]);
    const [hasMore, setHasMore] = useState(true);
    const [debounceTimeout, setDebounceTimeout] = useState(
        null as number | null,
    );

    const fetchGifs = useCallback(
        async (offset: number) => {
            const q = query.trim();
            if (!hasMore || q === "") {
                return;
            }

            try {
                const response = await giphy_search(q, offset);
                // data: GIF Object[]
                const newGifs: any[] = response.data;

                if (newGifs.length < SEARCH_LIMIT) {
                    // Здесь надо поставить флаг, что мы достигли конца пагинации, чтобы ограничить количество апи вызовов giphy_search, т.к. все последующие вызовы вернут пустой массив и будут тратить лимит giphy.
                    //setHasMore(false); // Dont work
                }
                setGifs((prev) => [...prev, ...newGifs]);
            } catch (error) {
                console.error("Error fetching GIFs:", error);
            }
        },
        [query, hasMore],
    );

    const onEndReached = (index: number) => fetchGifs(index + 1);

    useEffect(() => {
        if (debounceTimeout) {
            clearTimeout(debounceTimeout);
        }

        const timeout = window.setTimeout(async () => {
            setGifs([]);
            //setHasMore(true); // Dont work
            await fetchGifs(0); // Fetch GIFs based on the new query
        }, DEBOUNCE_TIMEOUT);

        setDebounceTimeout(timeout);

        return () => {
            clearTimeout(timeout); // Cleanup on unmount or query change
        };
    }, [query]);

    // Register mouse events to close
    const baseRef = useCloseHook(onClose);

    return (
        <Base gap="0" ref={baseRef}>
            <Controls>
                <InputBox
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.currentTarget.value)}
                    placeholder="Type to search GIF from giphy.com..."
                />
            </Controls>
            <Parent>
                <Virtuoso
                    data={gifs}
                    endReached={onEndReached}
                    itemContent={(_index, gif) => (
                        <div key={gif.id} onClick={() => onSelect?.(gif)}>
                            <img
                                src={gif.images.fixed_height_small.url}
                                alt={gif.title}
                            />
                        </div>
                    )}
                    style={{
                        flexGrow: 1,
                        padding: "0 2px",
                        overflowX: "hidden",
                    }}
                />
                {!hasMore && <div>No more data</div>}
            </Parent>
        </Base>
    );
}
